// This is the default theme.

// Many components can be themed even further than they are here.
// Inspect the styles and themeKeys of a component to see what is possible.

// Some components have a style hierarchy.
// E.g. defaultText > sectionTitle > cartCount

// Anything not specified (undefined) using the higher priority themeKey will fallback
// to the value provided by one of the lower priority themeKeys.

const DEFAULT_FONT_FAMILY = 'Roboto';

const GREEN = '#1D9B06';

const TRANSPARENT = 'transparent';
const BLACK = '#000000';
const WHITE = '#FFFFFF';
const OFF_BLACK = '#1B1B1B';
// const OFF_WHITE = '#fbfbfb';
const SUCCESS = '#259B36';
const DANGER = '#C21213';
const WARNING = '#ffc107';

const LIGHTESTGREY = '#EBEBEB';
// const LIGHTGREY = '#DFDFDF';
// const LINK = '#007bff';
const RED = '#C21213';

const OUTLINE = '#DFDFDF';
const OUTLINE_VARIANT = '#737373';

/**
 * Overridable Constants
 *
 * - Literally behaves like constants, but they can be
 * swapped out in theme overrides
 * - These can be overidden on polygon central too via
 * theming or by runtimeConfig and devThemeOverrides.
 *
 * > Note:
 * Component theme keys (i.e: dropDownInputError) will
 * override these constants
 *
 * Concept 8 has an example on how to set these constants.
 */
const Theme = (configConstants?: ThemeConstantsRaw) => {
  const c = {
    color: {
      primary: configConstants?.color?.primary ?? RED,
      onPrimary: configConstants?.color?.onPrimary ?? WHITE,

      secondary: configConstants?.color?.secondary ?? RED,

      success: configConstants?.color?.success ?? SUCCESS,
      danger: configConstants?.color?.danger ?? DANGER,
      warning: configConstants?.color?.warning ?? WARNING,

      onSurface: configConstants?.color?.onSurface ?? BLACK,
      outline: configConstants?.color?.outline ?? OUTLINE,
      outlineVariant: configConstants?.color?.outlineVariant ?? OUTLINE_VARIANT,

      surface: configConstants?.color?.surface ?? WHITE,
      surfaceVariant: configConstants?.color?.surfaceVariant ?? LIGHTESTGREY,
    },

    // Debug constants - temp
    /* color: {borderColor
      primary: '#00ff40',
      onPrimary: '#ff0073',
      onSurface: '#0012ff',
      outlineVariant: '#ddff00',
      outline: '#000',
      surface: '#ff0303',
      surfaceVariant: 'darkgreen',
    }, */

    /* color: {
      primary: '#97f9af',
      onPrimary: '#286435',
      onSurface: '#ffffff',
      outlineVariant: '#8f8dc2',
      outline: '#3f2c75',
      surface: '#0b0f24',
      surfaceVariant: '#273059',
    },*/

    // TODO make the TypographyTypes themeable
    type: {
      heading: {
        size: 22,
        weight: 500,
      },
      subHeading: {
        size: 20,
        weight: 500,
      },
      subHeading2: {
        size: 18,
        weight: 500,
      },
      subHeading3: {
        size: 14,
        weight: 600,
      },
      body: {
        size: 16,
        weight: 400,
      },
    },
  };

  return DefaultThemes(c);
};

/**
 * Default theme key value pairs
 * @param c
 */
const DefaultThemes = (c: ThemeConstants) => ({
  color: {
    chip: c.color.onSurface,
    adyenCardField: c.color.onSurface,
    adyenCardHelper: c.color.outlineVariant,
    adyenCardError: c.color.danger,
    miamRemoveButton: RED,
    miamCustomizeButton: RED,
    onPrimary: c.color.onPrimary,
    headerHamburgerButton: c.color.onSurface,
    completedOrderMessage: c.color.onSurface,
    barcodeMemberNumber: c.color.onSurface,
    closeIconColor: c.color.onSurface,
    fieldIcons: 0,
    textColor: c.color.onSurface,
    labelColor: c.color.outlineVariant, //focused or not
    formLabel: c.color.outlineVariant,
    fieldBorderFocus: c.color.primary,
    fieldDefaultBorder: c.color.outline,
    fieldHoverBorderColor: c.color.outlineVariant,
    fieldBackground: TRANSPARENT,
    helperColor: c.color.primary,
    helperBackground: TRANSPARENT,
    labelBackgroundColor: TRANSPARENT,
    materialTextFieldErrorBorder: c.color.primary,
    materialDatePickerPlaceholderColor: c.color.outlineVariant,
    caretColor: c.color.onSurface,
    checkboxLabelColor: c.color.onSurface,
    defaultCheckboxColor: c.color.onSurface,
    checkboxCheckedColor: c.color.primary,
    checkboxUncheckedColor: c.color.surfaceVariant,
    dateFilterTitle: c.color.onSurface,
    filterIconColors: c.color.onSurface,
    loyaltyHeaderInfo: c.color.onSurface,
    loyaltySubTitle: c.color.onSurface,
    loyaltyHeader: c.color.onSurface,
    loyaltyBalanceText: c.color.onSurface,
    loyaltyBalanceDefault: c.color.onSurface,
    loyaltyWalletTitle: c.color.onSurface,
    memberMoney: c.color.onSurface,
    memberPointsTitle: c.color.onSurface,
    balanceAmountMemberModal: c.color.onSurface,
    loyaltyGreeting: c.color.onSurface,
    loyaltyRing: c.color.primary,
    giftCardValueButton: c.color.outlineVariant,
    badgeOptionDefault: c.color.onSurface,
    badgeOptionActive: c.color.onPrimary,
    activeToggleText: c.color.onPrimary,
    activeHeartIcon: c.color.primary,
    resetFilters: c.color.outlineVariant,
    loyaltySaleType: c.color.primary,
    pastOrdersTitleSaleType: c.color.primary,
    loyaltyUserDetails: c.color.primary,
    balanceIconMemberModal: c.color.onSurface,
    balanceLabelMemberModal: c.color.onSurface,
    banner: c.color.onSurface,
    categoryBarCurrentIndicator: c.color.primary,
    menuBrandsBarCurrentIndicator: WHITE,
    categoryBarLabel: c.color.onSurface,
    menuBrandsBarLabel: c.color.onSurface,
    locationBrandBarLabel: c.color.onSurface,
    checkoutButton: c.color.onPrimary,
    choiceSetCheckbox: c.color.onPrimary,
    defaultButton: c.color.onPrimary,
    resendVerificationButton: c.color.onPrimary,
    verificationBannerText: WHITE,
    defaultText: c.color.onSurface,
    deliveryEstimateDetail: c.color.outlineVariant,
    deliveryTrackingLink: c.color.primary,
    emptyCartMessage: c.color.outlineVariant,
    giftCardApplyButton: c.color.onPrimary,
    headerBrandingName: c.color.onSurface,
    locationDetail: 'rgba(0,0,0,0.8)',
    locationDetailIcon: c.color.primary,
    lookupPromoCodeButton: WHITE,
    memberBalancesTitleCheckout: WHITE,
    menuCategoryHeadingCount: 'rgba(0,0,0,0.5)',
    menuItemDescription: c.color.outlineVariant,
    menuItemEnergySuffix: c.color.onSurface,
    menuItemSizeDescription: c.color.outlineVariant,
    menuSearchResultCount: c.color.outlineVariant,
    minimumOrderAmount: c.color.primary,
    modalButtonIcon: c.color.onSurface,
    modalButtonLabel: c.color.onSurface,
    noGuestOrderingLink: c.color.primary,
    offerInfoButton: c.color.primary,
    orderMessageIcon: 'orange',
    orderPropertyButton: c.color.onSurface,
    orderPropertyButtonIcon: c.color.primary,
    orderTotalDeemphasised: c.color.outlineVariant,
    paymentMethodIcon: c.color.primary,
    purchaseEditorDescription: c.color.outlineVariant,
    // purchaseEditorEnergySuffix: c.color.onSurface,
    purchaseEditorEnergy: c.color.onSurface,
    purchaseEditorEnergyInfoText: c.color.onSurface,
    purchaseEditorChoiceSetEnergy: c.color.onSurface,
    purchaseEditorChoiceSetEnergySuffix: c.color.onSurface,
    quantityPickerIcon: c.color.onPrimary,
    removeOfferButton: WHITE,
    removeSavedCardButton: c.color.outlineVariant,
    removeSavedCardButtonIcon: c.color.primary,
    saveCardIcon: c.color.primary,
    saveCardLabel: c.color.outlineVariant,
    savedCardNumber: c.color.outlineVariant,
    paymentMethodAvailableBalance: c.color.outlineVariant,
    scrollForMoreIndicator: WHITE,
    sectionTitle: c.color.outlineVariant,
    selectedTagButton: WHITE,
    signInLink: c.color.onSurface,
    signInLinkRight: c.color.primary,
    stagedPurchaseButton: 'rgba(0,0,0,0.9)',
    stagedPurchaseButtonIcon: c.color.primary,
    stagedPurchaseMultiplicationSymbol: c.color.primary,
    stagedPurchaseSubLine: c.color.outlineVariant,
    tagButton: c.color.onSurface,
    purchaserInvalidIcon: 'orange',
    locationMapMarker: c.color.primary,
    locationMapMarkerSelected: c.color.onPrimary,
    timeInErrorMessage: 'orange',
    deliveryDetailEditIcon: c.color.primary,
    menuLocationDetailsName: c.color.primary,
    menuLocationDetailsMobile: WHITE,
    appLoadingSpinner: WHITE,
    fallbackToPickupButton: c.color.primary,
    fraudPreventionMessageIcon: 'orange',
    noKnownUserLocationMessage: c.color.onSurface,
    memberButton: c.color.onPrimary,
    memberLoyaltyButtons: c.color.onSurface,
    signOutButton: c.color.onSurface,
    activeMemberButton: c.color.onSurface,
    activeSubButton: c.color.onSurface,
    activeMemberButtonLoyaltySideBar: c.color.primary,
    activeSubRoute: c.color.primary,
    defaultDropdownMenuButton: c.color.onSurface,
    defaultSubButton: c.color.onSurface,
    defaultMemberButtonLoyaltySideBar: c.color.onSurface,
    defaultSubRoute: c.color.onSurface,
    sliderIcon: c.color.onSurface,
    filterText: c.color.onSurface,
    orderOnlineButton: c.color.primary,
    loyaltyMigrationMenuButton: c.color.onSurface,
    createNewFavourite: c.color.onPrimary,
    cartItemError: '#E22C29',
    cartItemErrorAlert: '#E22C29',
    cartItemWarning: '#ff9800',
    cartItemWarningAlert: '#ff9800',
    STOCK_BALANCE_THRESHOLD_0: '#E22C29',
    STOCK_BALANCE_THRESHOLD_1: '#ff9800',
    STOCK_BALANCE_THRESHOLD_2: c.color.onSurface,
    STOCK_BALANCE_THRESHOLD_3: c.color.onSurface,
    STOCK_BALANCE_THRESHOLD_4: c.color.onSurface,
    saleTypeIcons: c.color.primary,
    pickSaleTypeDescription: c.color.primary,
    pickSaleTypeBottom: c.color.onSurface,
    viewCartButton: c.color.onPrimary,
    warningMessage: c.color.outlineVariant,
    transparentButton: c.color.primary,
    messageModalBorderButton: c.color.primary,
    underageButton: c.color.primary,
    orderStatusStepIcon: c.color.primary,
    miamRemoveBorderButton: c.color.primary,
    miamBorderButton: c.color.primary,
    orderStatusStepCompleted: WHITE,
    viewOrderDetailsButton: c.color.primary,
    addOrderToFavourites: c.color.primary,
    viewOrderDetailsTitle: c.color.primary,
    viewOrderInvoiceButton: c.color.primary,
    joinNowLoyaltyButton: c.color.primary,
    cancelMenuSearchButton: 'rgba(0,0,0,0.75)',
    modalSectionTitle: c.color.outlineVariant,
    clearAllButton: c.color.outlineVariant,
    unavailablePurchaseButton: c.color.primary,
    stagedPurchaseRemoveButtonText: c.color.primary,
    stagedPurchaseCustomiseButtonText: c.color.outlineVariant,
    cartShowDetails: c.color.onSurface,
    cvcHelper: c.color.outlineVariant,
    bannerLabelIcon: c.color.primary,
    customTagWarning: WHITE,

    automaticOfferText: c.color.primary,

    // new account screen stuff
    accountCardName: c.color.primary,
    stampcardProgressCounter: c.color.primary,

    accountBalanceAmount: c.color.primary,
    accountBalanceAmount_cr: c.color.primary,

    migrationFormLoginText: c.color.primary,

    progress: c.color.surfaceVariant,
    progressBar: c.color.primary,
    ratingFilled: c.color.primary,
    ratingUnFilled: c.color.surfaceVariant,
    starLabel: c.color.outlineVariant,

    choiceChecked: c.color.onPrimary,
    loyaltyOrderDate: c.color.outlineVariant,
    pastOrdersDate: c.color.outlineVariant,
    headerButton: c.color.outlineVariant,
    textButton: c.color.primary,
    outlineRoundButton: c.color.primary,
    offersMoreText: c.color.primary,
    tableRowHeader: c.color.outlineVariant,
    tableRowSubHeader: c.color.outlineVariant,
    addToOrderButton: c.color.onPrimary,
    itemChoiceActiveGrid: c.color.onPrimary,
    payWithMultiplePayment: c.color.primary,
    headerBackButton: c.color.onSurface,
    caretIcon: c.color.outlineVariant,
    scrollHandle: c.color.onPrimary,
    leftDrawerCloseIcon: c.color.onSurface,
    stripeElementText: c.color.onSurface,
    formTextFieldInput: c.color.onSurface,
    reviewOrderBackIcon: c.color.onSurface,
    menuFilterPrice: c.color.primary,
    choiceQuantityControl: c.color.onPrimary,
    resendCodeText: c.color.primary,
    resendCodeTextDisabled: c.color.surfaceVariant,
    defaultTransparentButton: c.color.outlineVariant,
    defaultPrimaryTransparentButton: c.color.primary,
    chevron: c.color.outlineVariant,
    pillIconActive: c.color.primary,
    pillTextActive: c.color.primary,
    subSectionActive: c.color.primary,
    addDeliveryAddressButton: c.color.primary,
    removeSavedAddressButton: c.color.primary,
    inlineAlertError: c.color.primary,
    inlineAlertWarning: c.color.primary,
    adjustPinButton: c.color.primary,
    adjustPinMarker: c.color.primary,
    editSavedAddressButton: c.color.primary,
    chooseOnMapText: c.color.primary,
    removeAddressDialogConfirm: c.color.outlineVariant,
    removeAddressDialogCancel: c.color.primary,
    savedAddressIcon: c.color.outlineVariant,
    savedAddressIconActive: c.color.primary,
    savedAddressTitle: BLACK,
    savedAddressSubtitle: c.color.outlineVariant,
    savedAddressTitleActive: c.color.primary,
    savedAddressSubtitleActive: c.color.primary,
    addressCheckIcon: c.color.primary,
    timeSelectorErrorText: c.color.outlineVariant,
    dateTimeInputLabel: c.color.outlineVariant,
  },

  backgroundColor: {
    chip: c.color.surface,
    adyenCardField: c.color.surface,
    chipHover: c.color.surfaceVariant,

    choiceChecked: c.color.primary,
    choiceUnChecked: c.color.surfaceVariant,

    // these three theme keys cost me 6 hours
    successSnackbar: '#43a047',
    warningSnackbar: '#ff9800',
    errorSnackbar: '#d32f2f',

    menuLocationDetails: c.color.surface,
    orderOnlineButton: TRANSPARENT,
    loyaltyMigrationMenuButton: TRANSPARENT,
    createNewFavourite: c.color.primary,
    giftCardValueButton: TRANSPARENT,
    badgeOptionDefault: c.color.surface,
    badgeOptionActive: c.color.primary,
    orderOnline: 'rgb(195,21,22)',
    paginationActive: BLACK,
    paginationDefault: TRANSPARENT,
    app: c.color.surface,
    appLoadingOverlay: 'rgba(0,0,0,0.75)',
    input: c.color.surface,
    balancesSeparatorCheckout: c.color.surface,
    balancesSeparatorMemberModal: BLACK,
    banner: OFF_BLACK,
    bannerWithProblem: '#bdbdbd',
    cancelMenuSearchButton: TRANSPARENT,
    fallbackToPickupButton: TRANSPARENT,

    choiceSetCheckbox: c.color.primary,

    defaultButton: c.color.primary,
    resendVerificationButton: c.color.primary,
    verificationBanner: c.color.primary,

    headerBarFloating: c.color.surface,
    headerBarHero: c.color.surface,
    headerQuantity: 'green',
    initialScreenVerticalSeparator: BLACK,

    locationListElementSelected: 'rgba(22, 151, 2, 0.1)',
    menuBrandScrollbar: c.color.surface,
    menuCategoriesScrollbar: c.color.surface,
    menuSearchBar: c.color.surface,
    modalButtonIcon: c.color.surface,
    modalOverlay: 'rgba(0,0,0,0.5)',
    modalOverlayCallout: 'rgba(0,0,0,0.3)',
    modalProceedButton: c.color.primary,
    orderTotalsSeperator: 'rgba(0,0,0,0.05)',
    removeOfferButton: c.color.primary,
    screen: c.color.surface,
    scrollForMoreIndicator: '#28dd08',
    selectedTagButton: GREEN,
    signOutButton: TRANSPARENT,
    tagButton: TRANSPARENT,
    reviewOrderScreenDesktopSideOuter: 'rgba(0,0,0,0.6)',
    menuItem: c.color.surface,
    fastOrderButton: OFF_BLACK,
    locationBrandBar: c.color.surface,
    noKnownUserLocationMessage: '#ffc107',
    menuItemOpacityBackstop: c.color.surface,
    cartItemErrorAlert: '#E22C29',
    cartItemWarningAlert: '#ff9800',
    memberButton: TRANSPARENT,
    memberLoyaltyButtons: TRANSPARENT,
    activeMemberButton: TRANSPARENT,
    activeSubButton: TRANSPARENT,
    defaultDropdownMenuButton: TRANSPARENT,
    defaultSubButton: TRANSPARENT,
    activeMemberButtonLoyaltySideBar: TRANSPARENT,
    defaultMemberButtonLoyaltySideBar: TRANSPARENT,
    defaultSubRoute: TRANSPARENT,
    activeSubRoute: TRANSPARENT,
    loyaltyCheckoutButton: c.color.primary,
    choiceQuantityControl: c.color.primary,
    choiceQuantityControlDisabled: c.color.surfaceVariant,
    buttonDisabled: c.color.surfaceVariant,
    locationItemSelected: c.color.surface,
    switchChecked: c.color.primary,
    pickSaleTypeBottom: TRANSPARENT,
    transparentButton: TRANSPARENT,
    messageModalBorderButton: TRANSPARENT,
    miamRemoveBorderButton: TRANSPARENT,
    miamBorderButton: TRANSPARENT,
    underageButton: TRANSPARENT,
    orderStatusStepCompleted: c.color.primary,
    addOrderToFavourites: TRANSPARENT,
    viewOrderDetailsButton: TRANSPARENT,
    viewOrderInvoiceButton: TRANSPARENT,
    orderStatusStepIcon: c.color.primary,
    orderStatusStepsConnector: c.color.primary,
    joinNowLoyaltyButton: TRANSPARENT,
    purchaseEditorBottomSection: c.color.surface,

    tagWarning: c.color.primary,
    customTagWarning: OFF_BLACK,

    accountCardContainer: c.color.surface,
    defaultStamp: c.color.surfaceVariant,
    defaultStampFulfilled: c.color.primary,
    defaultStampRewardIndicator: '#ffc107',
    stampImageContainer: c.color.surfaceVariant,
    stampImageContainerFulfilled: c.color.primary,

    creditLoyaltyProgressDefault: c.color.surfaceVariant,
    creditLoyaltyProgress: c.color.primary,
    itemChoiceActive: c.color.primary,
    itemChoiceActiveGrid: c.color.primary,
    outlineRoundButton: TRANSPARENT,
    modalCloseButton: c.color.surface,
    addToOrderButton: c.color.primary,
    dropdownMenuOptionHoverStyle: c.color.surfaceVariant,
    dropdownMenu: c.color.surface,
    horizontalLineText: c.color.outlineVariant,
    offer: c.color.surface,
    loyaltyBackground: c.color.surfaceVariant,
    loyaltyMobileBackground: c.color.surface,
    loyaltyModals: c.color.surface,
    locationModalBackground: c.color.surface,
    searchBar: c.color.surfaceVariant,
    scrollHandle: c.color.primary,
    leftDrawer: c.color.surface,
    locationItem: c.color.surface,
    locationListToggleHover: c.color.surfaceVariant,
    reviewOrderBottomBar: c.color.surface,
    reviewOrderMobileHeader: c.color.surface,
    pillActive: `${c.color.primary}33`, // 20% opacity
    pill: c.color.surfaceVariant,
    radioToggleInnerCircle: c.color.primary,
    inlineAlertError: `${c.color.primary}33`, // 20% opacity
    inlineAlertWarning: `${c.color.primary}33`, // 20% opacity
    inlineAlertInfo: `${c.color.outline}33`, // 20% opacity
    currentLocationMarker: c.color.primary,
    adjustPinButton: c.color.surface,
    selectedAddress: '#F4F6F8',
    adjustPinMoveMapIcon: c.color.primary,
  },
  borderColor: {
    defaultBorder: c.color.outline,
    adyenCardField: c.color.outline,
    adyenCardFieldHover: c.color.outlineVariant,
    adyenCardFieldFocus: c.color.primary,

    adyenCardError: c.color.danger,
    offer: c.color.outline,
    mfaCodeInput: c.color.primary,
  },
  fontFamily: {
    defaultText: DEFAULT_FONT_FAMILY,
    signInLink: DEFAULT_FONT_FAMILY, // TODO: rework this to use Text component
  },

  /**
   * If the Figma design specifies a font size that is not TypographyTypes from <Text>
   * You can add them here
   */

  fontSize: {
    adyenCardHelper: 12,
    miamCustomizeButton: 12,
    miamRemoveButton: 12,
    headerHamburgerButton: 24,
    subFeedback: c.type.subHeading2.size,
    completedOrderMessage: 22,
    barcodeMemberNumber: 14,
    giftCardValueButton: 15,
    badgeOptionDefault: 15,
    badgeOptionActive: 15,
    loyaltySubTitle: 15,
    orderOnline: 15,
    loyaltyHeaderInfo: 13,
    filterSaleTypes: 11,
    dateFilterTitle: 11,
    filterTitle: 22,
    resetFilters: 10,
    loyaltyHeader: undefined,
    loyaltyUserDetails: 14,
    defaultBarcode: 14,
    loyaltyBalanceDefault: 35,
    loyaltyRingView: 60,
    loyaltyGreeting: 20,
    appLoadingSpinner: 150,
    balanceAmountCheckout: 20,
    balanceAmountMemberModal: 20,
    balanceIconCheckout: 45,
    balanceIconMemberModal: 45,
    balanceLabelCheckout: 13,
    balanceLabelMemberModal: 13,
    checkoutSignInPrompt: 20,
    checkoutSignInPromptIcon: 40,
    choiceSetCheckbox: 20,
    choiceSetCheckboxIcon: 30,
    completedOrderDetail: 30,
    completedOrderTitle: 22,
    dynamicHeaderMemberButton: '3.5vw',
    defaultText: 16,
    deliveryDetailAddress: 15,
    deliveryDetailTime: 17,
    deliveryDetailTimeETA: 17,
    deliveryTimeSelect: 15,
    deliveryTrackingLink: 22,
    orderTypeDeliverySubtitleAddress: 16,
    editDeliveryEstimateButton: 15,
    emptyCartMessage: 15,
    giftCardApplyButton: 15,
    headerBackButton: 35,
    headerBackButtonIcon: 30,
    headerBrandingNameDesktop: 25,
    headerBrandingNameMobile: 15,
    dynamicCartIcon: '5vw',
    menuSearchIcon: 24,
    locationDetail: 12,
    locationDetailIcon: 14,
    locationListElementAddress: 13,
    locationListElementDetail: 13,
    locationListElementDistance: 12,
    locationSearchResultCount: 10,
    memberBalancesTitleCheckout: 20,
    menuCategoryHeading: undefined,
    menuCategoryHeadingCount: undefined,
    menuItemSizeDescription: 12,
    menuSearchResultCount: 13,
    modalButtonIcon: 32,
    modalButtonLabel: 16,
    modalProceedButton: 15,
    modalTitle: 30,
    noResultsInMenuSearchMessage: 20,
    orderMessage: 13,
    orderMessageIcon: 15,
    orderOfferButton: 16,
    orderPickupTimeSelect: 15,
    orderTableNumber: c.type.subHeading2.size,
    orderTotal: 14,
    orderTotalDeemphasised: 12,
    paymentMethod: 16,
    paymentMethodIcon: 22,
    paymentMethodUnavailableReason: 10,
    purchaseEditorEnergy: 16,
    purchaseEditorEnergyInfoText: 16,
    quantityPickerIcon: 25,
    cvcHelper: 12,
    saveCardLabel: 14,
    savedCardNumber: 10,
    paymentMethodAvailableBalance: 10,
    stagedPurchase: 16,
    stagedPurchaseButton: 11,
    stagedPurchaseButtonIcon: 15,
    stagedPurchaseSubLine: 13,
    tagButton: 15,
    timeInErrorMessage: 13,
    deliveryDetailEdit: 13,
    deliveryDetailEditIcon: 16,
    // giftCardApplyButtonIcon: c.type.subHeading2.size,
    menuLocationDetailsName: 10,
    locationBrandBarLabel: 15,
    fraudPreventionMessageIcon: 40,
    // orderStatusStepIcon: c.type.subHeading2.size,
    noKnownUserLocationMessageIcon: c.type.subHeading2.size,
    memberLoyaltyButtons: 16,
    signOutButton: 14,
    activeSubButton: 12,
    activeMemberButton: 14,
    orderOnlineButton: 14,
    loyaltyMigrationMenuButton: 14,
    defaultDropdownMenuButton: 14,
    defaultSubButton: 12,
    activeMemberButtonLoyaltySideBar: 14,
    activeSubRoute: 12,
    defaultMemberButtonLoyaltySideBar: 14,
    defaultSubRoute: 12,

    //olo3
    saleTypeIcons: 19,
    //pickSaleTypeDescription: 13,
    //pickSaleTypeBottom: 14,
    modalHeader: c.type.subHeading2.size,
    shortAddress: 12,
    locationListItemBestDeliveryTime: 12,
    viewCartButton: 14,
    //menuLocationDetailsSaleType: 12,
    noGuestOrderingLink: 14,
    warningMessage: 12,
    underageButton: 14,
    addOrderToFavourites: c.type.subHeading2.size,
    viewOrderDetailsButton: 17,
    joinLoyaltyTitle: 20,
    modalSectionTitle: 14,
    clearAllButton: 12,
    menuFilterTitle: 15,
    menuItemPrice: undefined,
    removeFiltersWarnings: 13,
    viewOrderDetailsLocation: 14,
    viewOrderDetailsTitle: 14,
    orderDetailsCart: 12,
    viewOrderInvoiceButton: 13,

    automaticOfferText: 11,

    // new accounts page keys
    accountCardName: 18,
    stampcardProgressCounter: 16,

    accountBalanceAmount: 40,
    // cr for credit loyalty
    accountBalanceAmount_cr: 36,

    migrationFormButton: 18,
    migrationFormLoginPretext: 14,
    migrationFormLoginText: 14,
    menuLocationDetailsNameMobile: 14,
    menuLocationDetailsAddressMobile: 14,
    removeSavedCardButton: 14,
    energyIntakeText: 14,
    footerLinkText: 12,

    communicationHeading: 18,
    communicationSubHeading: 16,

    textFieldCharacterCount: 10,

    pillText: 12,
    adjustPinButton: 14,
    editSavedAddressButton: 12,
  },
  fontWeight: {
    chip: '500',
    miamCustomizeButton: '500',
    miamRemoveButton: '500',
    transparentButton: '500',
    subFeedback: c.type.subHeading2.weight,
    completedOrderMessage: '500',
    barcodeMemberNumber: '500',
    badgeOptionDefault: '400',
    badgeOptionActive: '500',
    loyaltySubTitle: '500',
    dateFilterTitle: '500',
    loyaltySubHeaders: '600',
    loyaltyStoreLocation: '700',
    pastOrdersTitle: '700',
    favouriteOrderName: '700',
    historicalOrderDetailTitle: '700',
    historicalOrderTotals: '600',
    historicalOrderRewards: '600',
    filterTitle: '500',

    loyaltyOrderTotal: '600',
    pastOrdersTotal: '600',

    loyaltyHeader: '500',
    loyaltyUserDetails: '500',
    loyaltyWalletTitle: '500',
    loyaltyBalanceDefault: '500',
    loyaltyGreeting: '500',
    activeSubRoute: '500',
    activeSubButton: '500',
    activeMemberButtonLoyaltySideBar: '500',
    defaultMemberButtonLoyaltySideBar: '500',
    defaultSubRoute: '500',
    activeMemberButton: '500',
    defaultDropdownMenuButton: '400',
    orderOnlineButton: '500',
    loyaltyMigrationMenuButton: '500',
    defaultSubButton: '400',
    signOutButton: '400',
    balanceAmountMemberModal: '500',
    banner: '500',
    cartCount: '700',
    categoryBarLabel: '500',
    menuBrandsBarLabel: '500',
    menuBrandsBarLabelCurrent: '700',
    locationBrandBarLabel: '400',
    checkoutSignInPrompt: '500',
    choiceSetCheckbox: '500',
    completedOrderDetail: '700',
    completedOrderTitle: '500',
    completedOrderHeading: '700',
    defaultButton: '500',
    resendVerificationButton: '500',
    deliveryDetailTime: '500',
    deliveryDetailTimeETA: '500',
    deliveryTimeSelect: '500',
    deliveryTrackingLink: '700',
    // giftCardApplyButton: '400',
    headerBackButton: '500',
    headerMemberButton: '500',
    headerPickupTimeSelect: '500',
    headerTableNumber: '500',
    locationListElementAddress: '300',
    menuItemEnergySuffix: '700',
    menuItemName: '500',
    menuItemPrice: 'bold',
    minimumOrderAmount: '700',
    modalButtonLabel: '500',
    modalProceedButton: '500',
    modalTitle: '500',
    newOrderButton: '500',
    noGuestOrderingLink: '700',
    offerName: '500',
    orderMessage: '500',
    orderPickupTimeSelect: '500',
    orderPropertyButton: '500',
    orderTableNumber: '500',
    orderTotal: '700',
    orderTotalDeemphasised: '500',
    purchaseEditorEnergy: '700',
    purchaseEditorEnergyInfoText: '500',
    purchaseEditorChoiceSetEnergy: '400',
    purchaseEditorName: '500',
    removeSavedCardButton: '400',
    sectionTitle: '700',
    stagedPurchase: '500',
    stagedPurchaseButton: '400',
    stagedPurchaseMultiplicationSymbol: '700',
    tagButton: '400',
    deliveryDetailEdit: '500',
    // menuLocationDetailsName: '700',
    STOCK_BALANCE_THRESHOLD_0: '500',
    STOCK_BALANCE_THRESHOLD_1: '500',
    STOCK_BALANCE_THRESHOLD_2: '500',
    STOCK_BALANCE_THRESHOLD_3: '500',
    STOCK_BALANCE_THRESHOLD_4: '500',

    //olo3
    pickSaleType: '500',
    pickSaleTypeDescription: '500',
    pickSaleTypeBottom: '500',
    modalHeader: '600',
    viewCartButton: '500',
    menuLocationDetailsSaleType: '500',
    orderStatusStepIcon: '600',
    viewOrderDetailsTitle: 'bold',
    viewOrderDetailsLocation: 'bold',
    addOrderToFavourites: '500',
    joinLoyaltyTitle: '500',
    modalSectionTitle: 'bold',
    modalSubSectionTitle: '700',
    menuFilterTitle: '500',

    // stampcards / account page
    accountCardName: '500',
    stampcardProgressCounter: '700',
    accountBalanceAmount: '700',
    accountBalanceAmount_cr: '700',
    confirmChangesPreviousChangesTitle: '700',
    confirmChangesCurrentChangesTitle: '700',
    confirmChangesSubsectionTitle: '700',
    signInLink: '500',
    signInLinkRight: '500',
    resendCodeText: '500',
    resendCodeTextRight: '500',
    communicationHeading: '500',
    defaultTransparentButton: '500',
    defaultPrimaryTransparentButton: '500',
    removeSavedAddressButton: '500',
    adjustPinButton: '700',
    editSavedAddressButton: '500',
  },
  letterSpacing: {
    menuItemEnergySuffix: 1,
    purchaseEditorEnergySuffix: 1,
    purchaseEditorCheckBoxEnergySuffix: 1,
    savedCardNumber: 1,
  },
  opacity: {
    giftCardValueButton: 1,
    badgeOptionDefault: 1,
    badgeOptionActive: 1,
    orderOnline: 1,
    categoryBarLabel: 0.75,
    categoryBarLabelCurrent: 1,
    locationSearchResultCount: 0.9,
    memberLoyaltyButtons: 1,
    activeSubButton: 1,
    activeMemberButton: 1,
    activeSubRoute: 1,
    activeMemberButtonLoyaltySideBar: 1,
    defaultDropdownMenuButton: 1,
    orderOnlineButton: 1,
    defaultSubButton: 1,
    defaultMemberButtonLoyaltySideBar: 1,
    defaultSubRoute: 1,
    createNewFavourite: 1,
    unavailablePurchaseText: 0.65,
    chip: 0.9,
    stampImageUnfulfilled: 0.65,
  },
  padding: {
    badgeOptionDefault: '5px 5px',
    badgeOptionActive: '5px 5px',
    banner: '0px 20px',
    cancelMenuSearchButton: '0 10px',
    defaultButton: 15,
    resendVerificationButton: 5,
    deliveryTimeSelect: '5px',
    editDeliveryEstimateButton: 10,
    giftCardApplyButton: '11px 20px 11px 20px',
    headerBackButton: 0,
    headerBar: '10px 15px',
    headerMemberButton: 10,
    headerPickupTimeSelect: '3px 10px',
    headerTableNumber: '7px 0px',
    memberOffers: 13,
    newOrderButton: '0.925em 1.25em',
    modalProceedButton: '0.925em 1.25em',
    orderPickupTimeSelect: '5px',
    orderTableNumber: 10,
    removeSavedCardButton: '5px 0',
    stagedPurchaseButton: 0,
    tagButton: '9px 11px',
    quantityPickerIcon: 5,

    // olo3
    addOrderToFavourites: 'none',
    viewOrderDetailsButton: 'none',
    viewOrderInvoiceButton: 'none',
  },
  paddingLeft: {
    //activeMemberButton: '25px',
    //defaultDropdownMenuButton: '25px',
    //orderOnlineButton: '25px',
    purchaseEditorEnergySuffix: 2,
  },
  gap: {
    headerBar: 10,
  },
  borderRadius: {
    giftCardValueButton: 5,
    badgeOptionDefault: 20,
    badgeOptionActive: 20,
    choiceSetCheckbox: 0,
    defaultButton: 2,
    deliveryTimeSelect: 5,
    modalProceedButton: 5,
    orderPickupTimeSelect: 5,
    tagButton: 20,
    giftCardApplyButton: 5,
    orderOnline: 5,
    quantityPickerIcon: '50%',

    accountCardContainer: 20,
    defaultStamp: 20,

    stampImageContainer: 75,

    // olo3
    messageModalButton: 5,
    messageModalBorderButton: 5,
    miamBorderButton: 5,
    modalButtonIcon: '50%',
    outlineRoundButton: 25,
  },
  border: {
    loyaltyFilterBorder: `1px solid ${c.color.outlineVariant}`,
    outlineRoundButton: `1px solid ${c.color.primary}`,
    cardBorder: `1px solid ${c.color.outline}`,
    locationItem: `1px solid ${c.color.outline}`,
    locationListToggle: `1px solid ${c.color.outline}`,
    giftCardValueButton: '1px solid silver',
    defaultButton: 'none',
    resendVerificationButton: 'none',
    deliveryTimeSelect: '1px solid rgba(0, 0, 0, 0.1)',
    //modalProceedButton: '1px solid rgba(0, 0, 0, 0.1)',
    orderPickupTimeSelect: '1px solid rgba(0, 0, 0, 0.1)',
    orderTableNumber: '1px solid rgba(0, 0, 0, 0.35)',
    scrollForMoreIndicator: '1px solid rgba(0, 0, 0, 0.1)',
    selectedTagButton: '1px solid transparent',
    tagButton: '1px solid rgba(0, 0, 0, 0.1)',
    messageModalBorderButton: `2px solid ${c.color.primary}`,
    miamRemoveBorderButton: 'none',
    miamBorderButton: `2px solid ${c.color.primary}`,
    joinNowLoyaltyButton: `2px solid ${c.color.primary}`,
    defaultBorder: `1px solid ${c.color.outline}`,
    selected: `1px solid ${c.color.primary}`,
    pill: `2px solid ${c.color.outline}`,
    pillActive: `2px solid ${c.color.primary}`,
    subSectionActive: `2px solid ${c.color.primary}`,
    radioToggleCircle: `2px solid ${c.color.outline}`,
    radioToggleCircleChecked: `2px solid ${c.color.primary}`,
    inlineAlertError: `2px solid ${c.color.primary}`,
    inlineAlertWarning: `2px solid ${c.color.primary}`,
    inlineAlertInfo: `2px solid ${c.color.outline}`,
    adjustPinButton: `2px solid ${c.color.primary}`,
    locationListItem: `2px solid ${c.color.outline}`,
    locationListItemActive: `2px solid ${c.color.primary}`,
    chip: `2px solid ${c.color.outline}`,
    dayPartChip: `2px solid ${c.color.primary}`,
  },
  borderLeft: {
    defaultBorder: `1px solid ${c.color.outline}`,
    activeMemberButton: `5px solid ${c.color.primary}`,
  },
  borderTop: {
    defaultBorder: `1px solid ${c.color.outline}`,
    /* menuCategoriesScrollbar: `1px solid ${c.color.outline}`, */
  },
  borderRight: {
    defaultBorder: `1px solid ${c.color.outline}`,
  },
  borderBottom: {
    defaultBorder: `1px solid ${c.color.outline}`,
    tableRow: `1px solid ${c.color.outline}`,
    mfaCodeInput: `2px solid ${c.color.primary}`,
    subSectionActive: `2px solid ${c.color.primary}`,
  },
  outline: {
    menuItemHovered: '1.5px solid black',
  },
  boxShadow: {
    giftCardValueButton: 'none',
    badgeOptionDefault: 'none',
    badgeOptionActive: 'none',
    // https://tailwindcss.com/docs/box-shadow#outer-shadow
    cancelMenuSearchButton: 'none',
    completedOrderScreenDesktop:
      '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
    //defaultButton: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    // giftCardApplyButton: 'none',
    //.headerBar: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.09)',
    initialScreenDesktop: 'rgba(55, 71, 79, 0.35) 0 6px 40px 2px',
    menuCategoriesBar: 'rgba(0, 0, 0, 0.1) 0px 4px 16px -1px, rgba(0, 0, 0, 0.09) 0px 2px 2px -1px',
    modalCallout:
      '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
    reviewOrderScreenDesktop: '6px 6px 20px -6px rgba(0,0,0,0.75)',
    scrollForMoreIndicator: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    tagButton: 'none',
    memberLoyaltyButtons: 'none',
    signOutButton: 'none',
    activeMemberButton: 'none',
    activeSubButton: 'none',
    defaultDropdownMenuButton: 'none',
    orderOnlineButton: 'none',
    loyaltyMigrationMenuButton: 'none',
    defaultSubButton: 'none',
    activeMemberButtonLoyaltySideBar: 'none',
    defaultMemberButtonLoyaltySideBar: 'none',
    defaultSubRoute: 'none',
    activeSubRoute: 'none',

    //olo3
    pickSaleTypeBottom: 'none',
    transparentButton: 'none',
    messageModalBorderButton: 'none',
    miamRemoveBorderButton: 'none',
    miamBorderButton: 'none',
    addOrderToFavourites: 'none',
    viewOrderDetailsButton: 'none',
    viewOrderInvoiceButton: 'none',

    // stampcard
    accountCardContainer: '0 4px 4px 0 #00000040',
  },
  textDecoration: {
    deliveryTrackingLink: 'none',
    activeMemberButtonLoyaltySideBar: 'underline',
    activeSubRoute: 'underline',
  },
  maxHeight: {
    headerBrandingImage: '100%',
  },
  width: {
    stampImageContainer: 55,
  },
  height: {
    purchaseEditorItemImage: 160,
    stampImageContainer: 55,
    migrationFormButton: 50,
  },
  textAlign: {
    defaultBarcode: 'right',
    menuLocationDetailsSaleTypeMobile: 'center',
    messageModalMessage: 'center',
  },
  aspectRatio: {
    menuItemImage: '6/5',
  },
});

export default Theme;
